import { forwardRef, isValidElement, SyntheticEvent, useState } from 'react';
import { clsx } from '@team/clsx';
import { Cleaner } from '@team/cleaner';
import { Assert } from '@team/assert';
import { RootComponent } from '../../services/root-component';
import { Props } from './types';
import { State } from './enums';
import {
  getFetchPriorityProp,
  imageObjectFitPresets,
  imageStatePresets,
  rootStatePresets,
} from './model';
import css from './style.module.css';

export const Entry = forwardRef<HTMLDivElement, Props>(function Component(
  {
    /**
     * 💬 Деструктуризируем все пропсы для `img`
     */
    alt,
    crossOrigin,
    decoding,
    loading,
    referrerPolicy,
    sizes,
    src,
    srcSet,
    useMap,
    fetchPriority,
    width: imageWidth,
    height: imageHeight,
    onLoad,
    onError,
    objectFit = 'cover',
    size,
    imageRef,
    /**
     * 💬 Остальные применяем к `root` компоненту
     */
    style,
    fallback,
    children,
    withBorder = false,
    ...rootProps
  },
  forwardedRef
) {
  const hasSource = [src, srcSet].some(Assert.isString);

  const width = Cleaner.valueOrFallback(imageWidth, size);
  const height = Cleaner.valueOrFallback(imageHeight, size);

  const [state, setState] = useState<State>(
    hasSource ? State.LOADING : State.PLACEHOLDER
  );

  const handleImageLoad = (event: SyntheticEvent<HTMLImageElement>) => {
    /**
     * 💬 Бывают случаи, когда запрос падает с ошибкой,
     * но событие `onerror` не вызывается.
     * В таком случае проверяем натуральные размеры.
     * В случае, если ширина или высота нулевая,
     * то считаем, что нужно вызвать событие `onerror`.
     */
    if (
      event.currentTarget.naturalWidth === 0 ||
      event.currentTarget.naturalHeight === 0
    ) {
      handleImageError(event);

      return;
    }

    setState(State.LOADED);

    if (Assert.isDefined(onLoad)) {
      onLoad(event);
    }
  };

  const handleImageError = (event: SyntheticEvent<HTMLImageElement>) => {
    setState(State.FAILED);

    if (Assert.isDefined(onError)) {
      onError(event);
    }
  };

  const shouldShowFallback =
    [State.FAILED, State.PLACEHOLDER].includes(state) &&
    isValidElement(fallback);

  return (
    <RootComponent
      Component="div"
      baseClassName={clsx(css.root, rootStatePresets[state])}
      style={{ ...style, width, height }}
      {...rootProps}
      ref={forwardedRef}
    >
      {hasSource && (
        <img
          className={clsx(
            css.image,
            imageStatePresets[state],
            imageObjectFitPresets[objectFit]
          )}
          srcSet={srcSet}
          src={src}
          width={width}
          height={height}
          alt={alt}
          crossOrigin={crossOrigin}
          decoding={decoding}
          loading={loading}
          referrerPolicy={referrerPolicy}
          sizes={sizes}
          useMap={useMap}
          onLoad={handleImageLoad}
          onError={handleImageError}
          ref={imageRef}
          {...getFetchPriorityProp(fetchPriority)}
        />
      )}

      {shouldShowFallback && <div className={css.fallback}>{fallback}</div>}

      {children}

      {withBorder && <div className={css.border} aria-hidden />}
    </RootComponent>
  );
});
